import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@client/components/ui/popover';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { Checkbox } from '@client/components/ui/checkbox';
import { MoreHorizontal, Trash2, Mail, Search, ArrowUpDown, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useTeamTableState, SortColumn } from './useTeamTableState';
import { TeamTableSkeleton } from './TeamTableSkeleton';

interface TeamTableProps {
  relationships: AdvisorHierarchyRelationship[] | null;
  onDelete: (relationship: AdvisorHierarchyRelationship) => void;
  onResend?: (relationship: AdvisorHierarchyRelationship) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isAdminMode?: boolean;
}

export function TeamTable({
  relationships,
  onDelete,
  onResend,
  onRefresh,
  isRefreshing,
  isAdminMode = false,
}: TeamTableProps) {
  const {
    sortColumn,
    sortDirection,
    searchText,
    showAllStatuses,
    filteredAndSortedRelationships,
    handleSort,
    handleSearchChange,
    handleShowAllStatusesChange,
  } = useTeamTableState({ relationships });

  const getStatusTextClasses = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-700';
      case 'inactive':
        return 'text-gray-700';
      case 'sent':
        return 'text-blue-700';
      case 'declined':
        return 'text-red-700';
      default:
        return 'text-gray-700';
    }
  };

  const SortableHeader = ({
    column,
    children,
  }: {
    column: SortColumn;
    children: React.ReactNode;
  }) => {
    const isActive = sortColumn === column;
    const rotation = isActive && sortDirection === 'desc' ? 'rotate-180' : '';

    return (
      <button
        type="button"
        onClick={() => handleSort(column)}
        className="flex items-center gap-1 hover:text-gray-900"
      >
        <span>{children}</span>
        <ArrowUpDown
          className={`h-3.5 w-3.5 transition-transform ${rotation} ${isActive ? 'text-gray-900' : 'text-gray-500'}`}
        />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Input and Status Filter */}
      <div className="flex items-center justify-between">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder="Search advisors..."
            value={searchText}
            onChange={e => handleSearchChange(e.target.value)}
            className="pl-10 pr-9"
          />
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-all-statuses"
            checked={showAllStatuses}
            onCheckedChange={checked => handleShowAllStatusesChange(checked === true)}
          />
          <label
            htmlFor="show-all-statuses"
            className="cursor-pointer text-sm leading-none text-gray-700 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show all statuses
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader column="advisor">Advisor</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="status">Status</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="clientAccess">Client Access</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="startDate">Start Date</SortableHeader>
              </TableHead>
              {isAdminMode && (
                <TableHead>
                  <SortableHeader column="otherOrgs">Other Organizations</SortableHeader>
                </TableHead>
              )}
              <TableHead>
                <SortableHeader column="subscriptionOwner">Subscription Owner</SortableHeader>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isRefreshing ? (
              <TeamTableSkeleton />
            ) : !filteredAndSortedRelationships || filteredAndSortedRelationships.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAdminMode ? 7 : 6}
                  className="py-12 text-center"
                >
                  <p className="text-gray-700">
                    {searchText ? 'No matching team members found.' : 'No team members found.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedRelationships.map((relationship, idx) => (
                <TableRow key={relationship.invitationId || relationship.advisorId || idx}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {relationship.advisorFirstName && relationship.advisorLastName
                          ? `${relationship.advisorFirstName} ${relationship.advisorLastName}`
                          : relationship.advisorEmail}
                      </span>
                      <span className="text-sm text-gray-700">{relationship.advisorEmail}</span>
                      {relationship.organizationDisplayName || relationship.organizationName ? (
                        <span className="text-sm text-gray-700">
                          {relationship.organizationDisplayName || relationship.organizationName}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs font-medium capitalize ${getStatusTextClasses(relationship.relationshipStatus)}`}
                    >
                      {relationship.relationshipStatus}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-900">
                    {relationship.accessMode === 'specific'
                      ? 'Specific Clients'
                      : relationship.accessMode === 'full'
                        ? 'Yes'
                        : 'No'}
                  </TableCell>
                  <TableCell className="text-gray-900">
                    {relationship.createdDateTime &&
                    !isNaN(new Date(relationship.createdDateTime).getTime())
                      ? format(new Date(relationship.createdDateTime), 'MMM d, yyyy')
                      : '—'}
                  </TableCell>
                  {isAdminMode && (
                    <TableCell>
                      {relationship.otherSupervisors ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <span className="cursor-pointer text-gray-900 hover:text-blue-600 hover:underline">
                              {relationship.otherSupervisors.length > 20
                                ? `${relationship.otherSupervisors.slice(0, 20)}...`
                                : relationship.otherSupervisors}
                            </span>
                          </PopoverTrigger>
                          <PopoverContent
                            side="bottom"
                            align="start"
                            className="max-w-sm"
                          >
                            <p className="mb-1 text-xs font-medium text-gray-700">
                              Other Supervisors:
                            </p>
                            <p className="text-sm text-gray-900">{relationship.otherSupervisors}</p>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {relationship.subscriptionOwnerAdvisorId ? (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {relationship.subscriptionOwnerFirstName &&
                          relationship.subscriptionOwnerLastName
                            ? `${relationship.subscriptionOwnerFirstName} ${relationship.subscriptionOwnerLastName}`
                            : (relationship.subscriptionOwnerEmail ?? '—')}
                        </span>
                        <span className="text-sm text-gray-700">
                          {relationship.subscriptionOwnerOrgDisplayName ||
                            relationship.subscriptionOwnerOrgName ||
                            '—'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {relationship.relationshipStatus === 'active' ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <MoreHorizontal className="h-4 w-4 text-gray-900" />
                            <span className="sr-only">Open actions menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onDelete(relationship)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : relationship.relationshipStatus === 'sent' ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <MoreHorizontal className="h-4 w-4 text-gray-900" />
                            <span className="sr-only">Open actions menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {onResend && (
                            <DropdownMenuItem onClick={() => onResend(relationship)}>
                              <Mail className="mr-2 h-4 w-4" />
                              Resend Invitation
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => onDelete(relationship)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : relationship.relationshipStatus === 'declined' ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <MoreHorizontal className="h-4 w-4 text-gray-900" />
                            <span className="sr-only">Open actions menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onDelete(relationship)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
